import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Grid,
  Chip,
  Dialog,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination,
  Drawer,
  Fade,
  Slide,
  Grow,
  Skeleton,
  Switch,
  Tooltip,
  InputAdornment,
  Divider,
  Stack,
  Badge
} from '@mui/material';
import { MdDeleteForever, MdCloudUpload, MdCheckCircle, MdEdit, MdVisibility, MdClear, MdOutlineSearch, MdEmail, MdPhone, MdBusiness } from 'react-icons/md';
import { postData, getData, putData, deleteData, postFileUpload, patchData } from '../../Webservice/Webservice';
import SellerForm from '../../components/Seller/SellerForm';
import { MdOutlineInventory } from "react-icons/md";
import { IoMdMailOpen } from "react-icons/io";
import { FiFilter as FilterIcon, FiMapPin, FiPercent, FiCalendar } from 'react-icons/fi';
import { indianStates } from '../../Utils/states';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const statusConfig = {
  approved: { bg: '#def7ec', color: '#03543f', label: 'Approved' },
  rejected: { bg: '#fde8e8', color: '#9b1c1c', label: 'Rejected' },
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  waitingForApproval: { bg: '#e0f2fe', color: '#0369a1', label: 'Waiting' },
};

const initialSellerForm = {
  name: '',
  email: '',
  password: '',
  mobile: '',
  contactPerson2Name: '',
  contactPerson2Email: '',
  contactPerson2Mobile: '',
  contactPerson2Password: '',
  business_name: '',
  businessAddress: '',
  sellerLicenseNumber: '',
  businessCity: '',
  businessState: '',
  businessPincode: '',
  gstNumber: '',
  businessPAN: '',
  addressProof: null,
  gstProof: null,
  businessPanProof: null,
  shopActLicense: null,
  msmeUdyamCertificate: null,
  cancelledCheque: null,
  storeDescription: '',
  storeAddress: '',
  storeArea: '',
  storeNearbyLandmark: '',
  storeCity: '',
  storeState: '',
  storePincode: '',
  storeLatitude: '',
  storeLongitude: '',
  status: 'pending',
};

const Sellermanagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sellerId } = useParams();

  // State for Invite Mail dialog
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [mailEmail, setMailEmail] = useState('');
  const [mailSentSuccess, setMailSentSuccess] = useState(false);

  const handleOpenMailDialog = () => setMailDialogOpen(true);
  const handleCloseMailDialog = () => {
    setMailDialogOpen(false);
    setMailEmail('');
    setMailSentSuccess(false);
  };

  const handleSendMail = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await postData('seller/register-link', { email: mailEmail }, token);

      if (!(response && response.success)) {
        setMailSentSuccess(true);
        setTimeout(() => {
          handleCloseMailDialog();
        }, 2000);
      } else {
        alert(response?.message || `Registration link sent to: ${mailEmail}`);
        handleCloseMailDialog();
      }
    } catch (error) {
      console.error('Error sending registration link:', error);
      handleCloseMailDialog();
    }
  };

  // Highlight search term in text
  const highlightText = (text, highlight) => {
    if (!highlight || !highlight.trim() || !text) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ?
        <span key={i} style={{ backgroundColor: 'yellow' }}>{part}</span> :
        part
    );
  };

  const PAGE_SIZE = 20;
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [sellerForm, setSellerForm] = useState(initialSellerForm);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSellerId, setCurrentSellerId] = useState(null);
  const [formMode, setFormMode] = useState('edit');

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState(null);

  // Status toggle confirmation dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [sellerToChangeStatus, setSellerToChangeStatus] = useState(null);
  const [newStatusToApply, setNewStatusToApply] = useState('');

  // View drawer state
  const [viewSeller, setViewSeller] = useState(null);

  // Search & quick filters
  const [searchText, setSearchText] = useState('');
  const [quickStatus, setQuickStatus] = useState('');

  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    email: '',
    business_name: '',
    businessState: '',
    gstNumber: '',
    businessPAN: '',
    status: '',
    storeCity: '',
    storeState: '',
    minCommission: '',
    maxCommission: ''
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    name: '',
    mobile: '',
    email: '',
    business_name: '',
    businessState: '',
    gstNumber: '',
    businessPAN: '',
    status: '',
    storeCity: '',
    storeState: '',
    minCommission: '',
    maxCommission: ''
  });

  // Fetch sellers from API
  const fetchSellers = async () => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {
        page: meta.page,
        limit: PAGE_SIZE,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
        ...(searchText ? { name: searchText } : {}),
        ...(quickStatus ? { status: quickStatus } : {}),
      };
      const response = await getData('seller/list', params, token);
      if (response && Array.isArray(response.seller)) {
        setSellers(response.seller);
        const m = response.meta || { page: 1, totalPages: 1, total: 0 };
        setMeta({
          page: m.page,
          totalPages: m.totalPages,
          total: m.total
        });
      } else {
        setSellers([]);
        setMeta({ page: 1, totalPages: 1, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching sellers:', error);
      setSellers([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!sellerId) {
      fetchSellers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, filters, sellerId, searchText, quickStatus]);

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  // Status toggle (opens confirmation dialog)
  const handleStatusToggle = (seller) => {
    const newStatus = seller.status === 'approved' ? 'rejected' : 'approved';
    setSellerToChangeStatus(seller);
    setNewStatusToApply(newStatus);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSellerToChangeStatus(null);
    setNewStatusToApply('');
  };

  const handleConfirmStatusChange = async () => {
    if (!sellerToChangeStatus || !newStatusToApply) return;

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await putData(`seller/statusUpdate/${sellerToChangeStatus._id}`, { status: newStatusToApply }, token);

      // Update view drawer immediately
      setViewSeller(prev => prev && prev._id === sellerToChangeStatus._id ? { ...prev, status: newStatusToApply } : prev);

      // Refresh the full list from server
      await fetchSellers();
    } catch (error) {
      console.error('Error updating seller status:', error);
    }

    handleCloseStatusDialog();
  };

  const handleActiveToggle = async (seller) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await patchData(`seller/toggle-active/${seller._id}`, {}, token);
      if (response) {
        setSellers(prev => prev.map(s => s._id === seller._id ? { ...s, isActive: !s.isActive } : s));
        setViewSeller(prev => prev && prev._id === seller._id ? { ...prev, isActive: !prev.isActive } : prev);
      }
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const handleDeleteClick = (seller) => {
    setSellerToDelete(seller);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSellerToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (sellerToDelete) {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        await deleteData(`seller/delete/${sellerToDelete._id}`, token);
        // Refresh the full list from server
        await fetchSellers();
      } catch (error) {
        console.error('Error deleting seller:', error);
        alert('Error deleting seller');
      }
    }
    handleCloseDeleteDialog();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSellerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    setSellerForm(prev => ({ ...prev, [name]: file }));
  };

  const handleFormModeChange = () => {
    setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
  };

  // Navigate to edit seller
  const handleEditSeller = (seller) => {
    setFormMode('edit');
    navigate(`/sellermanagement/${seller._id}`, { state: { mode: 'edit' } });
  };

  // Navigate to view seller
  const handleViewSeller = (seller) => {
    setFormMode('view');
    navigate(`/sellermanagement/${seller._id}`, { state: { mode: 'view' } });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setQuickStatus('');
    const cleared = {
      name: '', mobile: '', email: '', business_name: '', businessState: '',
      gstNumber: '', businessPAN: '', status: '', storeCity: '', storeState: '',
      minCommission: '', maxCommission: ''
    };
    setFilters(cleared);
    setTempFilters(cleared);
    setMeta(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = searchText || quickStatus || Object.values(filters).some(x => x);

  useEffect(() => {
    const fetchSellerDetails = async () => {
      if (!sellerId) {
        setShowAddForm(false);
        setIsEditing(false);
        setCurrentSellerId(null);
        return;
      }

      setLoading(true);
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const response = await getData(`seller/${sellerId}`, {}, token);

        if (response) {
          setSellerForm({
            name: response.name || '',
            email: response.email || '',
            password: '',
            mobile: response.mobile || '',
            contactPerson2Name: response.contactPerson2Name || '',
            contactPerson2Email: response.contactPerson2Email || '',
            contactPerson2Mobile: response.contactPerson2Mobile || '',
            contactPerson2Password: '',
            business_name: response.business_name || '',
            businessAddress: response.businessAddress || '',
            sellerLicenseNumber: response.sellerLicenseNumber || '',
            businessCity: response.businessCity || '',
            businessState: response.businessState || '',
            businessPincode: response.businessPincode || '',
            gstNumber: response.gstNumber || '',
            businessPAN: response.businessPAN || '',
            addressProof: response.addressProof || null,
            gstProof: response.gstProof || null,
            businessPanProof: response.businessPanProof || null,
            shopActLicense: response.shopActLicense || null,
            msmeUdyamCertificate: response.msmeUdyamCertificate || null,
            cancelledCheque: response.cancelledCheque || null,
            storeDescription: response.storeDescription || '',
            storeAddress: response.storeAddress || '',
            storeArea: response.storeArea || '',
            storeNearbyLandmark: response.storeNearbyLandmark || '',
            storeCity: response.storeCity || '',
            storeState: response.storeState || '',
            storePincode: response.storePincode || '',
            storeLatitude: response.storeLatitude || '',
            storeLongitude: response.storeLongitude || '',
            status: response.status || 'pending'
          });
          setCurrentSellerId(sellerId);
          setIsEditing(true);
          // Default to view unless edit was explicitly requested via navigation state
          const requestedMode = location.state?.mode || 'view';
          setFormMode(requestedMode);
          setShowAddForm(true);
        } else {
          navigate('/sellermanagement');
        }
      } catch (error) {
        console.error('Error fetching seller details:', error);
        navigate('/sellermanagement');
      } finally {
        setLoading(false);
      }
    };

    fetchSellerDetails();
  }, [sellerId, navigate]);

  const handleAddSeller = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      const formData = new FormData();
      Object.keys(sellerForm).forEach(key => {
        if (sellerForm[key] !== null && sellerForm[key] !== undefined) {
          formData.append(key, sellerForm[key]);
        }
      });

      if (isEditing) {
        const response = await putData(`seller/update/${currentSellerId}`, formData, token);
        if (response) {
          alert('Seller updated successfully!');
          fetchSellers();
          setMeta(prev => ({ ...prev, page: 1 }));
        } else {
          alert('Failed to update seller.');
        }
      } else {
        const response = await postFileUpload('seller/add', formData, token);
        if (response) {
          alert('Seller added successfully!');
          fetchSellers(1);
          setMeta(prev => ({ ...prev, page: 1 }));
        } else {
          alert('Failed to add seller.');
        }
      }

      navigate('/sellermanagement');
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'adding'} seller:`, error);
      alert(`Error ${isEditing ? 'updating' : 'adding'} seller. Please try again.`);
    }

    setSellerForm(initialSellerForm);
    setShowAddForm(false);
    setIsEditing(false);
    setCurrentSellerId(null);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 1, md: 2 } }}>
      {!showAddForm ? (
        // Seller Table View
        <>
          {/* ── Header ─────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#111827' }}>
                Seller Management
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', fontFamily: 'Mulish, sans-serif' }}>
                {meta.total || sellers.length} total sellers
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search sellers..." value={searchText}
                onChange={e => { setSearchText(e.target.value); setMeta(prev => ({ ...prev, page: 1 })); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MdOutlineSearch size={18} color="#9ca3af" /></InputAdornment>,
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <MdClear size={15} color="#9ca3af" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: 220, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem', borderRadius: 2, bgcolor: '#fff' }
                }} />

              <TextField select size="small" label="Status" value={quickStatus}
                onChange={e => { setQuickStatus(e.target.value); setMeta(prev => ({ ...prev, page: 1 })); }}
                sx={{
                  minWidth: 160, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem', borderRadius: 2, bgcolor: '#fff' },
                  '& .MuiInputLabel-root': { fontFamily: 'Mulish, sans-serif' }
                }}>
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="waitingForApproval">Waiting For Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>

              {hasActiveFilters && (
                <Button size="small" variant="outlined" startIcon={<MdClear size={14} />}
                  onClick={clearFilters}
                  sx={{
                    fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                    borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px',
                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }, height: '40px'
                  }}>
                  Clear
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setTempFilters(filters);
                  setFilterDrawerOpen(true);
                }}
                sx={{
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                  borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px', height: '40px',
                  '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }, bgcolor: '#fff'
                }}
              >
                More Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<IoMdMailOpen />}
                color="primary"
                onClick={handleOpenMailDialog}
                sx={{
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                  borderColor: '#d1d5db', color: '#374151', borderRadius: '8px', height: '40px',
                  '&:hover': { bgcolor: '#f3f4f6', borderColor: '#9ca3af' }
                }}
              >
                Invite Mail
              </Button>
              <Button
                variant="contained"
                startIcon={<MdOutlineInventory />}
                color="primary"
                onClick={() => {
                  setShowAddForm(true);
                  setIsEditing(false);
                  setFormMode('edit');
                  setSellerForm(initialSellerForm);
                  if (sellerId) navigate('/sellermanagement');
                }}
                sx={{
                  fontFamily: 'Mulish, sans-serif', bgcolor: '#ffd102', color: '#000',
                  fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                  boxShadow: '0 4px 12px rgba(255,209,2,0.3)',
                  '&:hover': { bgcolor: '#f59e0b', boxShadow: '0 6px 16px rgba(255,209,2,0.4)' }
                }}
              >
                Add Seller
              </Button>
            </Box>
          </Box>

          {/* ── Filter Drawer ───────────────────────────────────────── */}
          <Drawer
            anchor="right"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
          >
            <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                Filters
              </Typography>

              <TextField fullWidth variant="outlined" label="Name" size="small" value={tempFilters.name}
                onChange={e => setTempFilters({ ...tempFilters, name: e.target.value })} />

              <TextField fullWidth variant="outlined" label="Mobile" size="small" value={tempFilters.mobile}
                onChange={e => setTempFilters({ ...tempFilters, mobile: e.target.value })} />

              <TextField fullWidth variant="outlined" label="Email" size="small" value={tempFilters.email}
                onChange={e => setTempFilters({ ...tempFilters, email: e.target.value })} />

              <TextField fullWidth variant="outlined" label="Business Name" size="small" value={tempFilters.business_name}
                onChange={e => setTempFilters({ ...tempFilters, business_name: e.target.value })} />

              <TextField
                select
                fullWidth
                variant="outlined"
                label="Business State"
                size="small"
                value={tempFilters.businessState}
                onChange={e => setTempFilters({ ...tempFilters, businessState: e.target.value })}
              >
                <MenuItem value="">Any State</MenuItem>
                {indianStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>

              <TextField fullWidth variant="outlined" label="GST Number" size="small" value={tempFilters.gstNumber}
                onChange={e => setTempFilters({ ...tempFilters, gstNumber: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })} />

              <TextField fullWidth variant="outlined" label="Business PAN" size="small" value={tempFilters.businessPAN}
                onChange={e => setTempFilters({ ...tempFilters, businessPAN: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })} />

              <TextField select fullWidth label="Status" variant="outlined" size="small" value={tempFilters.status}
                onChange={e => setTempFilters({ ...tempFilters, status: e.target.value })}>
                <MenuItem value="">Any Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="waitingForApproval">Waiting For Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>

              <TextField fullWidth variant="outlined" label="Store City" size="small" value={tempFilters.storeCity}
                onChange={e => setTempFilters({ ...tempFilters, storeCity: e.target.value })} />

              <TextField
                select
                fullWidth
                variant="outlined"
                label="Store State"
                size="small"
                value={tempFilters.storeState}
                onChange={e => setTempFilters({ ...tempFilters, storeState: e.target.value })}
              >
                <MenuItem value="">Any State</MenuItem>
                {indianStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>

              <Box display="flex" gap={1}>
                <TextField fullWidth variant="outlined" label="Min Commission" type="number" size="small" value={tempFilters.minCommission}
                  onChange={e => setTempFilters({ ...tempFilters, minCommission: e.target.value })} />
                <TextField fullWidth variant="outlined" label="Max Commission" type="number" size="small" value={tempFilters.maxCommission}
                  onChange={e => setTempFilters({ ...tempFilters, maxCommission: e.target.value })} />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    const cleared = {
                      name: '', mobile: '', email: '', business_name: '', businessState: '',
                      gstNumber: '', businessPAN: '', status: '', storeCity: '', storeState: '',
                      minCommission: '', maxCommission: ''
                    };
                    setTempFilters(cleared);
                    setFilters(cleared);
                    setMeta(prev => ({ ...prev, page: 1 }));
                    setFilterDrawerOpen(false);
                  }}
                  sx={{ color: 'grey', borderColor: 'grey' }}
                >
                  Clear
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setFilters(tempFilters);
                    setMeta(prev => ({ ...prev, page: 1 }));
                    setFilterDrawerOpen(false);
                  }}
                  sx={{
                    background: 'rgb(255 209 2)', color: 'black',
                    '&:hover': { background: 'rgb(255 209 2)' }
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Drawer>

          {/* ── Invite Mail Dialog ──────────────────────────────────── */}
          <Dialog open={mailDialogOpen} onClose={handleCloseMailDialog} maxWidth="xs" fullWidth>
            <Box p={3} sx={{ minHeight: 200, position: 'relative' }}>
              {mailSentSuccess ? (
                <Grow in={mailSentSuccess} timeout={500}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <Slide direction="up" in={mailSentSuccess} mountOnEnter unmountOnExit>
                      <Box>
                        <MdCheckCircle style={{
                          fontSize: 80,
                          color: '#4CAF50',
                          animation: 'pulse 1s infinite',
                        }} />
                      </Box>
                    </Slide>
                    <Fade in={mailSentSuccess} timeout={800}>
                      <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                        Registration Link Sent Successfully!
                      </Typography>
                    </Fade>
                    <Fade in={mailSentSuccess} timeout={1000}>
                      <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
                        Sent to: {mailEmail}
                      </Typography>
                    </Fade>
                  </Box>
                </Grow>
              ) : (
                <>
                  <Typography variant="h6" fontWeight={700} mb={2} textAlign="center"
                    sx={{ fontFamily: 'Mulish, sans-serif' }}>Invite Mail</Typography>
                  <TextField
                    fullWidth
                    label="Enter Email"
                    type="email"
                    value={mailEmail}
                    onChange={e => setMailEmail(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                  />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button onClick={handleCloseMailDialog} color="error" variant="outlined">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSendMail}
                      variant="contained"
                      sx={{
                        color: 'black',
                        fontFamily: 'Mulish, sans-serif',
                        background: 'rgb(255 209 2)',
                        '&:hover': { background: 'rgb(255 209 2)' },
                      }}
                      disabled={!mailEmail}
                    >
                      Send
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Dialog>

          {/* ── Table ────────────────────────────────────────────────── */}
          <Box sx={{
            bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden'
          }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      bgcolor: '#fafafa', fontFamily: 'Mulish, sans-serif',
                      fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                      letterSpacing: '0.05em', color: '#6b7280',
                      borderBottom: '2px solid #e5e7eb', py: 1.5, whiteSpace: 'nowrap'
                    }
                  }}>
                    <TableCell>Seller</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Verify Status</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell><Skeleton height={46} /></TableCell>
                        <TableCell><Skeleton height={46} /></TableCell>
                        <TableCell><Skeleton height={46} /></TableCell>
                        <TableCell><Skeleton height={46} /></TableCell>
                        <TableCell><Skeleton height={46} /></TableCell>
                        <TableCell><Skeleton height={46} /></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    sellers.length > 0 ? sellers.map(seller => {
                      const sc = statusConfig[seller.status] || statusConfig.pending;
                      const isApproved = seller.status === 'approved';
                      return (
                        <TableRow
                          key={seller._id}
                          hover
                          onClick={() => handleViewSeller(seller)}
                          sx={{
                            '&:hover': { backgroundColor: '#f9fafb', cursor: 'pointer' },
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          {/* Seller Name + Business */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={<Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff', bgcolor: isApproved ? '#10b981' : seller.status === 'rejected' ? '#ef4444' : '#f59e0b' }} />}>
                                <Box sx={{
                                  width: 40, height: 40, borderRadius: '50%', bgcolor: '#ffd102', color: '#000',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '0.85rem', border: '2px solid #fff',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)', fontFamily: 'Mulish, sans-serif'
                                }}>
                                  {(seller.name || '')[0]?.toUpperCase() || 'S'}
                                </Box>
                              </Badge>
                              <Box>
                                <Typography sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.9rem', color: '#111827' }}>
                                  {highlightText(seller.name, searchText || filters.name)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'Mulish, sans-serif' }}>
                                  {seller.business_name || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Contact */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <MdEmail size={13} color="#9ca3af" />
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#374151' }}>
                                  {seller.email || '—'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <MdPhone size={13} color="#9ca3af" />
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.8rem', color: '#6b7280' }}>
                                  {seller.mobile || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Location */}
                          <TableCell>
                            <Typography fontWeight={500} sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', fontSize: '0.9rem' }}>
                              {seller.businessCity || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'Mulish, sans-serif', color: '#9ca3af' }}>
                              {seller.businessState || ''}
                            </Typography>
                          </TableCell>

                          {/* Verification Status */}
                          <TableCell align="center">
                            <Tooltip title={`Click to ${isApproved ? 'Reject' : 'Approve'}`}>
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                <Switch
                                  checked={isApproved}
                                  onChange={() => handleStatusToggle(seller)}
                                  size="small"
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                                    '& .MuiSwitch-track': { bgcolor: seller.status === 'rejected' ? '#ef4444' : '#f59e0b' },
                                    '& .MuiSwitch-switchBase': { color: seller.status === 'rejected' ? '#ef4444' : '#f59e0b' },
                                  }}
                                />
                                <Chip
                                  label={sc.label}
                                  size="small"
                                  sx={{
                                    fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.6rem',
                                    textTransform: 'uppercase', borderRadius: '6px',
                                    backgroundColor: sc.bg, color: sc.color, border: 'none', height: 20
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>

                          {/* Active Toggle */}
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Switch
                              checked={!!seller.isActive}
                              onChange={() => handleActiveToggle(seller)}
                              size="small"
                              color="success"
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handleViewSeller(seller); 
                                  }}
                                  sx={{ bgcolor: '#eff6ff', color: '#3b82f6', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#dbeafe' } }}>
                                  <MdVisibility size={17} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Seller">
                                <IconButton size="small" 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handleEditSeller(seller); 
                                  }}
                                  sx={{ bgcolor: '#fefce8', color: '#ca8a04', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#fef9c3' } }}>
                                  <MdEdit size={17} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Seller">
                                <IconButton size="small" 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handleDeleteClick(seller); 
                                  }}
                                  sx={{ bgcolor: '#fef2f2', color: '#dc2626', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#fee2e2' } }}>
                                  <MdDeleteForever size={18} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <img style={{ width: '180px' }} src='/img/nodata.png' alt='no data' />
                            <Typography variant="h6" sx={{ fontFamily: "Mulish, sans-serif", color: "text.secondary" }}>
                              {hasActiveFilters ? 'No sellers match your filters' : 'No sellers found'}
                            </Typography>
                            {hasActiveFilters && (
                              <Button size="small" onClick={clearFilters} sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none' }}>
                                Clear filters
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e5e7eb' }}>
              <Pagination
                count={meta.totalPages}
                page={meta.page}
                onChange={handlePageChange}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: 'Mulish, sans-serif',
                    '&.Mui-selected': { bgcolor: '#ffd102', color: '#000' }
                  }
                }}
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>

          {meta.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
            </Typography>
          )}

          {/* ═══ VIEW DRAWER ══════════════════════════════════════════ */}
          <Drawer anchor="right" open={!!viewSeller} onClose={() => setViewSeller(null)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}>
            {viewSeller && (
              <>
                {/* Header */}
                <Box sx={{ bgcolor: '#111827', p: 3, pb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <IconButton onClick={() => setViewSeller(null)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#fff' } }}>
                      <MdClear size={20} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 80, height: 80, borderRadius: '50%', bgcolor: '#ffd102', color: '#000',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: '1.8rem', border: '4px solid rgba(255,255,255,0.15)',
                      fontFamily: 'Mulish, sans-serif'
                    }}>
                      {(viewSeller.name || '')[0]?.toUpperCase() || 'S'}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#fff', fontSize: '1.2rem' }}>
                        {viewSeller.name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#9ca3af', fontSize: '0.85rem' }}>
                        #{viewSeller._id?.slice(-8).toUpperCase()}
                      </Typography>
                      <Chip label={viewSeller.business_name || 'Seller'} size="small"
                        sx={{ mt: 0.5, fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.72rem', bgcolor: 'rgba(255,209,2,0.15)', color: '#ffd102', textTransform: 'uppercase' }} />
                    </Box>

                    {/* Inline status toggle in drawer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.08)', px: 2, py: 1, borderRadius: 2 }}>
                      <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#9ca3af' }}>Status:</Typography>
                      <Switch checked={viewSeller.status === 'approved'}
                        onChange={() => handleStatusToggle(viewSeller)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                          '& .MuiSwitch-track': { bgcolor: viewSeller.status === 'rejected' ? '#ef4444' : '#f59e0b' },
                          '& .MuiSwitch-switchBase': { color: viewSeller.status === 'rejected' ? '#ef4444' : '#f59e0b' },
                        }} />
                      <Typography sx={{
                        fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.82rem',
                        color: viewSeller.status === 'approved' ? '#10b981' : viewSeller.status === 'rejected' ? '#ef4444' : '#f59e0b'
                      }}>
                        {(statusConfig[viewSeller.status] || statusConfig.pending).label}
                      </Typography>
                    </Box>

                    {/* Active/Inactive toggle in drawer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.08)', px: 2, py: 1, borderRadius: 2 }}>
                      <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#9ca3af' }}>Active:</Typography>
                      <Switch checked={!!viewSeller.isActive}
                        onChange={() => handleActiveToggle(viewSeller)}
                        size="small"
                        color="success"
                      />
                      <Typography sx={{
                        fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.82rem',
                        color: viewSeller.isActive ? '#10b981' : '#9ca3af'
                      }}>
                        {viewSeller.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Details */}
                <Box sx={{ p: 3, overflowY: 'auto' }}>
                  {[
                    { label: 'Email Address', value: viewSeller.email, icon: <MdEmail size={16} /> },
                    { label: 'Phone Number', value: viewSeller.mobile, icon: <MdPhone size={16} /> },
                    { label: 'Business Name', value: viewSeller.business_name, icon: <MdBusiness size={16} /> },
                    { label: 'GST Number', value: viewSeller.gstNumber || '—', icon: null },
                    { label: 'Business PAN', value: viewSeller.businessPAN || '—', icon: null },
                    { label: 'Business Location', value: [viewSeller.businessCity, viewSeller.businessState].filter(Boolean).join(', ') || '—', icon: <FiMapPin size={16} /> },
                    { label: 'Store Location', value: [viewSeller.storeCity, viewSeller.storeState].filter(Boolean).join(', ') || '—', icon: <FiMapPin size={16} /> },
                    { label: 'Commission', value: viewSeller.commissionPercentage != null ? `${viewSeller.commissionPercentage}%` : '—', icon: <FiPercent size={16} /> },
                    { label: 'Joined', value: viewSeller.createdAt ? new Date(viewSeller.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—', icon: <FiCalendar size={16} /> },
                  ].map((row, i, arr) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.icon && <Box sx={{ color: '#9ca3af' }}>{row.icon}</Box>}
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#6b7280' }}>{row.label}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#111827', maxWidth: '55%', textAlign: 'right', wordBreak: 'break-word' }}>
                        {row.value || '—'}
                      </Typography>
                    </Box>
                  ))}

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1.5}>
                    <Button fullWidth variant="outlined" startIcon={<MdEdit size={16} />}
                      onClick={() => { setViewSeller(null); handleEditSeller(viewSeller); }}
                      sx={{
                        fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                        borderColor: '#ffd102', color: '#92400e',
                        '&:hover': { bgcolor: '#fffbeb', borderColor: '#f59e0b' }
                      }}>
                      Edit
                    </Button>
                    <Button fullWidth variant="outlined" startIcon={<MdDeleteForever size={16} />}
                      onClick={() => { setViewSeller(null); handleDeleteClick(viewSeller); }}
                      sx={{
                        fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                        borderColor: '#fca5a5', color: '#dc2626',
                        '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' }
                      }}>
                      Delete
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Drawer>

          {/* ═══ DELETE DIALOG ════════════════════════════════════════ */}
          <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}
            PaperProps={{
              style: {
                borderRadius: '10px',
                padding: '20px',
                minWidth: '400px'
              }
            }}>
            <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
              <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
                {sellerToDelete && (
                  <>Are you sure you want to delete seller <b>{sellerToDelete.name}</b>?</>
                )}
              </DialogContentText>
              <Box sx={{
                textAlign: 'center',
                backgroundColor: '#fff3cd',
                padding: '12px',
                borderRadius: '4px',
                borderLeft: '4px solid #ffc107',
                marginBottom: '2px'
              }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
                  Warning
                </Typography>
                <Typography variant="body2" sx={{ color: '#856404' }}>
                  By deleting, you won't be able to access it again.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2 }}>
              <Button
                onClick={handleCloseDeleteDialog}
                variant="outlined"
                sx={{
                  minWidth: '120px',
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                  fontWeight: 600, borderColor: '#e5e7eb', color: '#6b7280',
                  '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' }
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                sx={{
                  minWidth: '120px',
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                  fontWeight: 700, bgcolor: '#dc2626',
                  '&:hover': { bgcolor: '#b91c1c' }
                }}
                autoFocus
              >
                Yes, Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* ═══ STATUS CONFIRMATION DIALOG ════════════════════════════════ */}
          <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog}
            PaperProps={{
              style: {
                borderRadius: '10px',
                padding: '20px',
                minWidth: '400px'
              }
            }}>
            <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
              Confirm Status Change
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
                {sellerToChangeStatus && (
                  <>
                    Are you sure you want to change the status of <b>{sellerToChangeStatus.name}</b> to{' '}
                    <b style={{ color: newStatusToApply === 'approved' ? '#10b981' : '#ef4444' }}>
                      {newStatusToApply.toUpperCase()}
                    </b>?
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2, pt: 1 }}>
              <Button
                onClick={handleCloseStatusDialog}
                variant="outlined"
                sx={{
                  minWidth: '120px',
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                  fontWeight: 600, borderColor: '#e5e7eb', color: '#6b7280',
                  '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' }
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={handleConfirmStatusChange}
                variant="contained"
                sx={{
                  minWidth: '120px',
                  fontFamily: 'Mulish, sans-serif', textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: newStatusToApply === 'approved' ? '#10b981' : '#ef4444',
                  '&:hover': { bgcolor: newStatusToApply === 'approved' ? '#059669' : '#dc2626' }
                }}
                autoFocus
              >
                Yes, {newStatusToApply === 'approved' ? 'Approve' : 'Reject'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        // Add/Edit Seller Form View
        <SellerForm
          sellerForm={sellerForm}
          handleFormChange={handleFormChange}
          handleFileUpload={handleFileUpload}
          handleAddSeller={handleAddSeller}
          isEditing={isEditing}
          setShowAddForm={(val) => {
            if (!val) {
              navigate('/sellermanagement');
              setShowAddForm(false);
            } else {
              setShowAddForm(val);
            }
          }}
          formMode={formMode}
          handleFormModeChange={handleFormModeChange}
        />
      )
      }
    </Box >
  );
};

export default Sellermanagement;